import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, ChevronRight } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { ProfileView } from '@/components/resource-center/profile/ProfileView'
import { useResources } from '@/hooks/useResourceCenter'
import { PERMISSIONS, usePermission } from '@/lib/permissions'

/**
 * One person's resource profile (z8nrz7f0vw, deck slides 4 and 9) on its own
 * page: groups, methods, split, shift, support, absences, supervisor and lab
 * role, and their daily capacity. Reached from a name on Resources → People.
 */
export function ResourceProfilePage() {
  const { userId = '' } = useParams<{ userId: string }>()
  const { canAny, can } = usePermission()
  const canRead = canAny([PERMISSIONS.resourceCenterRead, PERMISSIONS.resourceCapabilitiesRead])
  const canManage = can(PERMISSIONS.resourceCapabilitiesManage)
  const resources = useResources()
  const name = resources.data?.find(r => r.userId === userId)?.name

  return (
    <div className='min-h-full w-full bg-muted/30 px-4 py-5 sm:px-6 lg:px-8'>
      <div className='w-full space-y-4'>
        <nav aria-label='Breadcrumb' className='flex items-center gap-1.5 text-sm text-muted-foreground'>
          <Link
            to='/resources'
            className='inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 font-medium text-primary hover:bg-primary/5'
          >
            <ArrowLeft className='h-4 w-4' /> Resources
          </Link>
          <ChevronRight className='h-3.5 w-3.5' />
          <Link to='/resources' className='hover:text-foreground'>
            People
          </Link>
          <ChevronRight className='h-3.5 w-3.5' />
          <span className='truncate font-medium text-foreground'>{name ?? 'Profile'}</span>
        </nav>
        {canRead ? (
          <ProfileView key={userId} userId={userId} canManage={canManage} />
        ) : (
          <Card>
            <CardContent className='p-6 text-sm text-muted-foreground'>
              You don't have access to resource profiles in this tenant.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
